import React, { useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Facebook, Settings, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

interface FacebookEmbedProps {
  pageId?: string;
  pageName?: string;
}

export const FacebookEmbed: React.FC<FacebookEmbedProps> = ({ 
  pageId,
  pageName 
}) => {
  const navigate = useNavigate();
  const embedRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  // Use a reliable public page as fallback for demo
  const displayPageId = pageId || 'facebook';
  const displayPageName = pageName || 'Facebook';

  console.log('📘 FacebookEmbed: Rendering embed for pageId:', displayPageId, 'pageName:', displayPageName);

  useEffect(() => {
    setIsLoading(true);
    setHasError(false);

    // Set a timeout to handle slow loading
    const timeoutId = setTimeout(() => {
      if (isLoading) {
        console.log('📘 FacebookEmbed: Timeout reached, assuming success or showing fallback');
        setIsLoading(false);
      }
    }, 10000); // 10 second timeout

    // Initialize Facebook SDK if not already loaded
    if (!window.FB) {
      console.log('📘 FacebookEmbed: Loading Facebook SDK...');
      
      // Create Facebook root div if it doesn't exist
      if (!document.getElementById('fb-root')) {
        const fbRoot = document.createElement('div');
        fbRoot.id = 'fb-root';
        document.body.appendChild(fbRoot);
      }

      // Load Facebook SDK
      const script = document.createElement('script');
      script.src = 'https://connect.facebook.net/en_US/sdk.js#xfbml=1&version=v18.0';
      script.async = true;
      script.defer = true;
      script.crossOrigin = 'anonymous';
      
      script.onload = () => {
        console.log('📘 FacebookEmbed: Facebook SDK loaded');
        if (window.FB && embedRef.current) {
          renderFacebookEmbed();
        }
      };

      script.onerror = () => {
        console.error('📘 FacebookEmbed: Failed to load Facebook SDK');
        setHasError(true);
        setIsLoading(false);
        clearTimeout(timeoutId);
      };

      document.head.appendChild(script);
    } else {
      console.log('📘 FacebookEmbed: Facebook SDK already loaded');
      renderFacebookEmbed();
    }

    return () => {
      clearTimeout(timeoutId);
    };
  }, [displayPageId]);

  const renderFacebookEmbed = () => {
    if (!embedRef.current) return;

    // Clear existing content
    embedRef.current.innerHTML = '';

    // Use iframe approach directly for better reliability
    const iframe = document.createElement('iframe');
    iframe.src = `https://www.facebook.com/plugins/page.php?href=https%3A%2F%2Fwww.facebook.com%2F${encodeURIComponent(displayPageId)}&tabs=timeline&width=340&height=400&small_header=true&adapt_container_width=true&hide_cover=false&show_facepile=false`;
    iframe.width = '100%';
    iframe.height = '400';
    iframe.frameBorder = '0';
    iframe.scrolling = 'no';
    iframe.style.border = 'none';
    iframe.style.overflow = 'hidden';
    iframe.allowFullscreen = true;
    iframe.allow = 'autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share';
    iframe.title = `${displayPageName} Facebook Feed`;
    
    iframe.onload = () => {
      console.log('📘 FacebookEmbed: Facebook iframe loaded successfully');
      setIsLoading(false);
    };
    
    iframe.onerror = () => {
      console.error('📘 FacebookEmbed: Facebook iframe failed to load');
      setHasError(true);
      setIsLoading(false);
    };
    
    embedRef.current.appendChild(iframe);
    
    // Backup timeout
    setTimeout(() => {
      if (isLoading) {
        console.log('📘 FacebookEmbed: Iframe timeout - assuming loaded');
        setIsLoading(false);
      }
    }, 6000);
  };

  const renderFallbackIframe = () => {
    return (
      <iframe
        src={`https://www.facebook.com/plugins/page.php?href=https%3A%2F%2Fwww.facebook.com%2F${encodeURIComponent(displayPageId)}&tabs=timeline&width=340&height=400&small_header=true&adapt_container_width=true&hide_cover=false&show_facepile=false`}
        width="100%"
        height="400"
        style={{ border: 'none', overflow: 'hidden' }}
        scrolling="no"
        frameBorder="0"
        allowFullScreen={true}
        allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
        title={`${displayPageName} Facebook Feed`}
        onLoad={() => setIsLoading(false)}
        onError={() => setHasError(true)}
      />
    );
  };

  return (
    <Card className="h-[500px]">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Facebook className="h-5 w-5 text-blue-600" />
            Facebook Feed {displayPageName && `(${displayPageName})`}
          </CardTitle>
          <Button 
            onClick={() => navigate('/dashboard-sidebar/platforms')}
            variant="ghost" 
            size="sm"
          >
            <Settings className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-2">
        <div className="w-full h-[400px] relative">
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                <p className="text-sm text-gray-600">Loading Facebook feed...</p>
              </div>
            </div>
          )}
          
          {hasError ? (
            <div className="h-full flex flex-col items-center justify-center bg-gray-50 p-4">
              <AlertCircle className="h-8 w-8 text-yellow-500 mb-2" />
              <p className="text-sm text-gray-600 mb-4 text-center">
                Facebook widget unavailable. Trying fallback...
              </p>
              {renderFallbackIframe()}
            </div>
          ) : (
            <div 
              ref={embedRef} 
              className="w-full h-full"
              style={{ minHeight: '400px' }}
            />
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Extend the Window interface to include FB
declare global {
  interface Window {
    FB: any;
  }
}